import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import { isSmallShopMode, normalizeExcelHeaderForSmallShop } from '../../utils/smallShopMode'
import * as XLSX from 'xlsx'
const transformExcelForSmallShop = async (file) => {
    const data = await file.arrayBuffer()
    const workbook = XLSX.read(data, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    const sheet = workbook.Sheets[sheetName]
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })

    if (rows.length > 0 && Array.isArray(rows[0])) {
        rows[0] = rows[0].map(normalizeExcelHeaderForSmallShop)
    }

    const newSheet = XLSX.utils.aoa_to_sheet(rows)
    workbook.Sheets[sheetName] = newSheet

    const output = XLSX.write(workbook, {
        bookType: file.name.match(/\.csv$/i) ? 'csv' : 'xlsx',
        type: 'array'
    })

    const mimeType = file.name.match(/\.csv$/i)
        ? 'text/csv'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

    return new File([output], file.name, { type: mimeType })
}

const promptBranchSelection = async (branches) => {
    if (branches.length === 0) {
        await Swal.fire({
            icon: 'error',
            title: 'No Branches Found',
            text: 'Please add a branch before importing stock.',
        })
        return null
    }

    if (branches.length === 1) {
        return branches[0].id
    }

    const optionsHtml = branches
        .map((branch) => `<option value="${branch.id}">${branch.name || `Branch ${branch.id}`}</option>`)
        .join('')

    const result = await Swal.fire({
        title: 'Select Branch',
        html: `
            <p style="margin-bottom: 12px; font-size: 14px; color: #555;">
                Choose which branch this import should be applied to.
            </p>
            <select id="import_branch_id" class="swal2-select" style="width: 100%; padding: 8px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px;">
                <option value="">Select Branch</option>
                ${optionsHtml}
            </select>
        `,
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
        focusConfirm: false,
        preConfirm: () => {
            const branchId = document.getElementById('import_branch_id')?.value
            if (!branchId) {
                Swal.showValidationMessage('Please select a branch')
                return false
            }
            return parseInt(branchId, 10)
        }
    })

    return result.isConfirmed ? result.value : null
}

const ImportFromExcel = () => {
    const smallShopMode = isSmallShopMode()
    const [file, setFile] = useState(null)
    const [loading, setLoading] = useState(false)
    const [previewData, setPreviewData] = useState([])
    const [showPreview, setShowPreview] = useState(false)
    const [branches, setBranches] = useState([])

    useEffect(() => {
        if (!smallShopMode) return

        const fetchBranches = async () => {
            try {
                const response = await apiClient.get(API_ENDPOINTS.BRANCHES)
                const branchList = response.data?.results || response.data || []
                setBranches(branchList)
            } catch (error) {
                console.error('Error fetching branches:', error)
            }
        }

        fetchBranches()
    }, [smallShopMode])

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0]
        if (selectedFile) {
            // Validate file type
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ]

            if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Type',
                    text: 'Please select a valid Excel file (.xlsx, .xls) or CSV file (.csv)',
                })
                e.target.value = ''
                return
            }

            setFile(selectedFile)
            previewFile(selectedFile)
        }
    }

    const previewFile = (file) => {
        const reader = new FileReader()

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, { type: 'array' })
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })

                // Get first 6 rows for preview (including header)
                const preview = jsonData.slice(0, 6)
                if (preview.length > 0 && Array.isArray(preview[0]) && isSmallShopMode()) {
                    preview[0] = preview[0].map(normalizeExcelHeaderForSmallShop)
                }
                setPreviewData(preview)
                setShowPreview(true)
            } catch (error) {
                console.error('Error reading file:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to read the file. Please ensure it is a valid Excel or CSV file.',
                })
            }
        }

        reader.readAsArrayBuffer(file)
    }

    const handleImport = async () => {
        if (!file) {
            Swal.fire({
                icon: 'warning',
                title: 'No File Selected',
                text: 'Please select a file to import.',
            })
            return
        }

        try {
            setLoading(true)

            let branchId = null
            if (smallShopMode) {
                branchId = await promptBranchSelection(branches)
                if (!branchId) {
                    setLoading(false)
                    return
                }
            }

            const uploadFile = smallShopMode ? await transformExcelForSmallShop(file) : file

            const formData = new FormData()
            // Backend expects 'file' field name (see backendcode.md line 516: request.FILES.get('file'))
            formData.append('file', uploadFile)
            if (smallShopMode && branchId) {
                formData.append('branch_id', branchId)
            }
            
            // Debug: verify file is in FormData
            console.log('File being uploaded:', file.name, file.type, file.size)
            console.log('File is File instance:', file instanceof File)
            for (const pair of formData.entries()) {
                console.log('FormData entry:', pair[0], pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1])
            }

            const endpoint = smallShopMode
                ? API_ENDPOINTS.BRANCH_STOCK_IMPORT_EXCEL
                : API_ENDPOINTS.IMPORT_PRODUCTS
            console.log('Uploading to endpoint:', endpoint)

            // Don't set Content-Type - let axios/browser set it automatically with boundary
            // This is crucial for multipart/form-data to work correctly
            const response = await apiClient.post(endpoint, formData)

            Swal.fire({
                icon: 'success',
                title: 'Import Successful',
                text: response.data.message || 'Products imported successfully!',
            })

            // Reset form
            setFile(null)
            setPreviewData([])
            setShowPreview(false)
            document.getElementById('fileInput').value = ''
        } catch (error) {
            console.error('Error importing products:', error)
            console.error('Error response:', error.response?.data)
            const errorMessage = error.response?.data?.detail || 
                               error.response?.data?.message || 
                               error.response?.data?.error ||
                               'Failed to import products. Please check your file format and try again.'
            Swal.fire({
                icon: 'error',
                title: 'Import Failed',
                text: errorMessage,
            })
        } finally {
            setLoading(false)
        }
    }

    const downloadTemplate = () => {
        // Create a sample template
        const template = [
            ['Product Name', 'SKU', 'Barcode', 'Category', 'Unit', 'Price', 'Cost', 'Description'],
            ['Sample Product 1', 'SKU001', '1234567890123', 'Electronics', 'Piece', '100.00', '80.00', 'Sample description'],
            ['Sample Product 2', 'SKU002', '9876543210987', 'Clothing', 'Piece', '50.00', '30.00', 'Another sample'],
        ]

        const ws = XLSX.utils.aoa_to_sheet(template)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Products')

        // Generate and download
        XLSX.writeFile(wb, 'product_import_template.xlsx')
    }

    return (
        <>
            <div className="pagetitle">
                <h1>Import Products From Excel</h1>
                <nav>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><a href="/">Home</a></li>
                        <li className="breadcrumb-item">Products</li>
                        <li className="breadcrumb-item active">Import From Excel</li>
                    </ol>
                </nav>
            </div>

            <section className="section">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                <div
                                    className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                                    style={{ paddingBottom: '15px', borderBottom: '2px solid #e9ecef' }}
                                >
                                    <div className="d-flex flex-column">
                                        <h5 className="card-title mb-1">
                                            <i className="bi bi-file-earmark-excel me-2" />
                                            Import Products From Excel
                                        </h5>
                                        <small className="text-muted">
                                            Upload an Excel or CSV file to import multiple products at once.
                                        </small>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={downloadTemplate}
                                    >
                                        <i className="bi bi-download me-1" />
                                        Download Template
                                    </button>
                                </div>

                                {/* Instructions */}
                                <div className="alert alert-info" role="alert">
                                    <h6 className="alert-heading">
                                        <i className="bi bi-info-circle me-2" />
                                        Instructions
                                    </h6>
                                    <ul className="mb-0" style={{ fontSize: '0.875rem' }}>
                                        <li>Download the template file to see the required format</li>
                                        <li>Fill in your product data following the template structure</li>
                                        <li>Supported formats: Excel (.xlsx, .xls) and CSV (.csv)</li>
                                        <li>Make sure all required fields are filled (Product Name, SKU, Price)</li>
                                        {smallShopMode && (
                                            <>
                                                <li>Use <strong>branch_id</strong> (not warehouse_id) when specifying a branch for stock in the file</li>
                                                {branches.length > 1 && (
                                                    <li>You will be asked to select a branch before the import starts</li>
                                                )}
                                            </>
                                        )}
                                        <li>Upload your file and click "Import Products" to complete the process</li>
                                    </ul>
                                </div>

                                {/* File Upload */}
                                <div className="mb-4">
                                    <label htmlFor="fileInput" className="form-label fw-bold">
                                        Select File
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        id="fileInput"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={handleFileChange}
                                    />
                                    {file && (
                                        <small className="text-muted mt-1 d-block">
                                            Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
                                        </small>
                                    )}
                                </div>

                                {/* Preview */}
                                {showPreview && previewData.length > 0 && (
                                    <div className="mb-4">
                                        <h6 className="fw-bold mb-2">
                                            <i className="bi bi-eye me-2" />
                                            Preview (First 5 rows)
                                        </h6>
                                        <div className="table-responsive" style={{ maxHeight: '300px', overflow: 'auto' }}>
                                            <table className="table table-bordered table-sm">
                                                <thead className="table-light">
                                                    <tr>
                                                        {previewData[0]?.map((header, index) => (
                                                            <th key={index} style={{ minWidth: '120px' }}>
                                                                {header}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {previewData.slice(1).map((row, rowIndex) => (
                                                        <tr key={rowIndex}>
                                                            {row.map((cell, cellIndex) => (
                                                                <td key={cellIndex}>{cell}</td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Import Button */}
                                <div className="d-flex justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setFile(null)
                                            setPreviewData([])
                                            setShowPreview(false)
                                            document.getElementById('fileInput').value = ''
                                        }}
                                        disabled={!file || loading}
                                    >
                                        <i className="bi bi-x-circle me-1" />
                                        Clear
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleImport}
                                        disabled={!file || loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                Importing...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-upload me-1" />
                                                Import Products
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default ImportFromExcel
